import { Crown } from "lucide-react";
import React from "react";

export interface PlanTagProps {
    planId: string;
    planName: string;
    compact?: boolean;
}

export const PlanTag = ({ planId, planName, compact }: PlanTagProps) => {
    const upperPlanId = planId.toUpperCase();
    // BUSINESS plans start with CUSTOM_ prefix (e.g., CUSTOM_BUSINESS, CUSTOM_ENTERPRISE)
    const isBusiness = upperPlanId.startsWith('CUSTOM_');
    
    // Special BUSINESS/CUSTOM styling - premium gold colors, same shape
    if (isBusiness) {
        const businessStyle: React.CSSProperties = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            padding: compact ? '2px 8px' : '4px 12px',
            borderRadius: '6px',
            fontSize: compact ? '10px' : '11px',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
            color: '#ffffff',
            boxShadow: '0 2px 10px rgba(245, 158, 11, 0.4)',
            border: 'none',
        };
        
        return (
            <span style={businessStyle}>
                <Crown size={10} style={{ color: '#FEF3C7' }} />
                {planName}
            </span>
        );
    }
    
    // Standard plan styles
    const planStyles: Record<string, React.CSSProperties> = {
        FREE: { background: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB' },
        STARTER: { background: 'linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(13, 148, 136, 0.3)' },
        ESSENTIAL: { background: 'linear-gradient(135deg, #0369A1 0%, #0EA5E9 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(3, 105, 161, 0.3)' },
        PRO: { background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(124, 58, 237, 0.35)' },
        TEAM: { background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(79, 70, 229, 0.3)' },
        DEVELOPER: { background: 'linear-gradient(135deg, #334155 0%, #64748B 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(51, 65, 85, 0.35)' },
        ADVANCED: { background: 'linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)' },
        PREMIUM: { background: 'linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)' },
        CUSTOM: { background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(217, 119, 6, 0.35)' },
        ENTERPRISE: { background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(217, 119, 6, 0.35)' },
    };
    
    const defaultStyle = { background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)', color: '#fff', boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)' };
    const planSpecificStyle = planStyles[upperPlanId] || defaultStyle;
    
    const style: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: compact ? '2px 8px' : '4px 12px',
        borderRadius: '6px',
        fontSize: compact ? '10px' : '11px',
        fontWeight: 600,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        border: 'none',
        ...planSpecificStyle,
    };
    
    return (
        <span style={style}>
            {upperPlanId !== 'FREE' && <Crown size={10} style={{ opacity: 0.9 }} />}
            {planName}
        </span>
    );
};

// Helper to check if a plan should show a crown
export const isPaidPlan = (planId?: string): boolean => {
    if (!planId) return false;
    return planId.toUpperCase() !== 'FREE';
};

export default PlanTag;
